import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Edit, Delete, Info, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { ProductGroup } from '../../models/ProductGroup';
import { useProductGroup } from '../../context/ProductGroupContext';
import { toast } from 'react-toastify';

interface ProductGroupCardProps {
  group: ProductGroup;
  onEdit: () => void;
  adminMode?: boolean;
}

const ProductGroupCard: React.FC<ProductGroupCardProps> = ({ 
  group, 
  onEdit,
  adminMode = true
}) => {
  const [expanded, setExpanded] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { deleteProductGroup } = useProductGroup();

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  const handleDelete = async () => {
    if (isDeleting) return;
    
    if (window.confirm(`Czy na pewno chcesz usunąć grupę "${group.name}"?`)) {
      try {
        setIsDeleting(true);
        await deleteProductGroup(group.id);
        toast.success("Grupa została pomyślnie usunięta");
      } catch (error: any) {
        toast.error(error.message || "Błąd podczas usuwania grupy");
        console.error("Error deleting group:", error);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  return (
    <motion.div 
      className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm"
      whileHover={{ boxShadow: "0 4px 6px rgba(0,0,0,0.05)" }}
      transition={{ duration: 0.3 }}
    >
      <div className="p-5">
        <div className="flex justify-between items-center">
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold text-gray-800">{group.name}</h3>
            <p className="text-sm text-gray-500">
              {group.products ? `${group.products.length} produktów` : "0 produktów"}
            </p>
          </div>
          <div className="flex space-x-2">
            {adminMode && (
              <>
                <motion.button
                  onClick={onEdit}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-full"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Edit fontSize="small" />
                </motion.button>
                <motion.button
                  onClick={handleDelete}
                  className={`p-2 text-red-600 hover:bg-red-50 rounded-full ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}`}
                  whileHover={isDeleting ? {} : { scale: 1.1 }}
                  whileTap={isDeleting ? {} : { scale: 0.9 }}
                  disabled={isDeleting}
                >
                  <Delete fontSize="small" />
                </motion.button>
              </>
            )}
            <motion.button
              onClick={toggleExpanded}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-full"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              {expanded ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
            </motion.button>
          </div>
        </div>
        
        {group.description && (
          <div className="mt-2 text-gray-600 text-sm">{group.description}</div>
        )}
      </div>

      {expanded && group.products && group.products.length > 0 && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="border-t border-gray-100 bg-gray-50 p-4"
        >
          <h4 className="text-sm font-medium text-gray-700 mb-3">Produkty w grupie:</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {group.products.map((product) => (
              <motion.div
                key={product.id}
                className="bg-white p-3 rounded-md border border-gray-200 flex items-center space-x-3"
                whileHover={{ y: -2, boxShadow: "0 4px 6px rgba(0,0,0,0.07)" }}
              >
                {product.mobilePrimaryImage ? (
                  <img 
                    src={product.mobilePrimaryImage} 
                    alt={product.name} 
                    className="h-12 w-12 object-contain bg-white rounded border border-gray-100"
                  />
                ) : (
                  <div className="h-12 w-12 flex items-center justify-center bg-gray-100 rounded border border-gray-200">
                    <Info fontSize="small" className="text-gray-400" />
                  </div>
                )}
                <div className="overflow-hidden">
                  <p className="text-sm font-medium text-gray-800 truncate">
                    {product.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {product.serialNumber}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default ProductGroupCard;
